import { Link } from "react-router";
import { Alert } from "@/components/ds";
import type { AuthBillingState, AuthMembership } from "@/client/hooks/useAuth";
import styles from "./BillingBanner.module.css";

interface BillingBannerProps {
  readonly billingState: AuthBillingState;
  readonly membership: AuthMembership;
}

type BannerContent = {
  readonly variant: "info" | "warning" | "danger";
  readonly title: string;
  readonly body: string;
  readonly cta: string;
};

function buildContent(state: AuthBillingState): BannerContent | null {
  switch (state.label) {
    case "trial_ending": {
      const days = state.trialDaysRemaining ?? 0;
      const plural = days === 1 ? "day" : "days";
      return {
        variant: "info",
        title: `Your trial ends in ${days} ${plural}`,
        body: "Add a payment method to keep TidyRota running without a break.",
        cta: "Add payment method",
      };
    }
    case "trial_expired":
      return {
        variant: "warning",
        title: "Your trial has ended",
        body: "Add a payment method to continue using TidyRota.",
        cta: "Add payment method",
      };
    case "past_due":
      return {
        variant: "warning",
        title: "We couldn't charge your card",
        body: "Update your payment details to avoid an interruption.",
        cta: "Update card",
      };
    case "cancelled":
      return {
        variant: "info",
        title: "Your subscription is cancelled",
        body: "You can restart it any time from the billing page.",
        cta: "Restart subscription",
      };
    default:
      return null;
  }
}

export function BillingBanner({ billingState, membership }: BillingBannerProps) {
  if (!billingState.showsBanner) return null;
  if (!membership.isOwner) return null;

  const content = buildContent(billingState);
  if (!content) return null;

  return (
    <div className={styles.wrapper}>
      <Alert variant={content.variant} title={content.title}>
        <div className={styles.row}>
          <span>{content.body}</span>
          <Link to="/billing" className={styles.cta}>
            {content.cta}
            <span aria-hidden="true" className={styles.arrow}>→</span>
          </Link>
        </div>
      </Alert>
    </div>
  );
}
